/**
 * PipelineStage1Reruns — output re-runs for the DS processing stage, rendered
 * inside the stage row's "Output re-runs" disclosure. Every row overwrites its
 * output in place (no duplicate tabs/files). Ported from PipelineRerunPanel.
 */

import React, { useState } from 'react';
import { Button } from '../../ui';
import { PipelineRun, PipelineRerunStep } from '../../../services/adminApi';

interface PipelineStage1RerunsProps {
  pipeline: PipelineRun;
  /** Any pipeline action in flight (disables the rows) */
  actionBusy: string | null;
  onRerun: (step: PipelineRerunStep) => Promise<{ runId: string; message: string }>;
  /** Full stage-1 re-run (re-processes everything from the stored upload) */
  onRestart: () => void;
  onViewRunLog: () => void;
}

const STAGE1_STEPS: Array<{ step: PipelineRerunStep; label: string; detail: string }> = [
  { step: 'master-summary', label: 'Master + Summary sheets', detail: 'Monthly Ads Sold master + CM-upload summary report (rewritten in place)' },
  { step: 'tracker-distribution', label: 'Channel Monthly Tracker distribution', detail: 'Per-channel month tabs with line items + Direct Sold summary' },
];

const PipelineStage1Reruns: React.FC<PipelineStage1RerunsProps> = ({
  pipeline,
  actionBusy,
  onRerun,
  onRestart,
  onViewRunLog,
}) => {
  const [notice, setNotice] = useState<string | null>(null);
  const partialReruns = ((pipeline.stages.dsProcessing.result as any) || {}).partialReruns || {};
  const dsRunning = pipeline.stages.dsProcessing.status === 'running';
  const dsFailed = pipeline.stages.dsProcessing.status === 'failed';

  const fire = async (step: PipelineRerunStep, label: string) => {
    if (!window.confirm(
      `Re-run ${label} for ${pipeline.monthDisplay}? This replaces the existing output in place — no duplicates are created.`
    )) return;
    setNotice(null);
    try {
      const res = await onRerun(step);
      setNotice(`${res.message} — track progress in the DS Processing tab (run ${res.runId}).`);
    } catch {
      // error is surfaced by the controller
    }
  };

  return (
    <div style={styles.panel}>
      {dsFailed && (
        <div style={styles.failedHint}>
          The last full run failed or was cancelled. These re-runs read the stored GAM upload and
          rewrite one output family in place — they do not need a completed run and do not reset
          the ChannelMeter / payout stages the way &quot;Retry processing&quot; does.
        </div>
      )}
      {STAGE1_STEPS.map(({ step, label, detail }) => {
        const scopeKey = step === 'master-summary' ? 'masterSummary' : 'trackerDistribution';
        const stamp = partialReruns[scopeKey];
        return (
          <div key={step} style={styles.row}>
            <div style={styles.rowMain}>
              <span style={styles.rowLabel}>{label}</span>
              <span style={styles.rowDetail}>
                {stamp ? `Last re-run ${new Date(stamp.at).toLocaleString()} by ${stamp.by}` : detail}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={dsRunning || actionBusy !== null}
              onClick={() => fire(step, label)}
            >
              {actionBusy === `rerun:${step}` ? 'Starting…' : 'Re-run'}
            </Button>
          </div>
        );
      })}
      <div style={styles.row}>
        <div style={styles.rowMain}>
          <span style={styles.rowLabel}>Full processing re-run</span>
          <span style={styles.rowDetail}>Re-process everything from the stored GAM upload with the current rate card</span>
        </div>
        <Button variant="ghost" size="sm" disabled={dsRunning || actionBusy !== null} onClick={onRestart}>
          {actionBusy === 'start' ? 'Starting…' : 'Re-run all'}
        </Button>
      </div>
      {notice && (
        <div style={styles.notice}>
          {notice}{' '}
          <button type="button" style={styles.link} onClick={onViewRunLog}>
            View run log →
          </button>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  panel: {
    background: 'var(--bg-surface-3)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
    padding: '4px 12px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid var(--border-subtle)',
  },
  rowMain: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  rowLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  rowDetail: {
    fontSize: 11.5,
    color: 'var(--fg-3)',
  },
  notice: {
    padding: '8px 0',
    fontSize: 12,
    color: 'var(--success)',
  },
  failedHint: {
    padding: '8px 0',
    fontSize: 12,
    color: 'var(--fg-3)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  link: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--fg-link)',
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'none',
  },
};

export default PipelineStage1Reruns;
